import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { getPostBySlug, getRecentPosts } from '../../services/blogServices';
import { BlogPost as BlogPostType } from '../../types/BlogTypes';
import './BlogPost.css';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPostType[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (slug) {
      const foundPost = getPostBySlug(slug);
      
      if (foundPost) {
        setPost(foundPost);
        // Obter posts recentes para a seção "Leia também"
        setRelatedPosts(getRecentPosts(3).filter(p => p.id !== foundPost.id));
      } else {
        navigate('/blog', { replace: true });
      }
    }
    
    setLoading(false);
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando artigo...</p>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="blog-post-container">
      <div className="blog-post-header">
        <div className="post-meta-top">
          <Link to="/blog" className="back-to-blog">← Voltar para o blog</Link>
          <span className="post-category">{post.category}</span>
        </div>
        <h1>{post.title}</h1>
        <div className="post-meta">
          <div className="post-author-date">
            <span className="post-author">Por {post.author}</span>
            <span className="post-date">Publicado em {post.date}</span>
          </div>
          <div className="post-tags">
            {post.tags.map(tag => (
              <Link key={tag} to={`/blog?tag=${encodeURIComponent(tag)}`} className="post-tag">
                {tag}
              </Link>
            ))}
          </div>
        </div>
      </div>
      
      <div className="featured-image">
        <img src={post.image} alt={post.title} />
      </div>
      
      <div className="blog-post-content">
        <div className="markdown-content">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </div>
        
        <div className="blog-post-footer">
          <div className="share-section">
            <p>Compartilhe este artigo:</p>
            <div className="share-buttons">
              <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="share-button twitter">
                Twitter
              </a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="share-button linkedin">
                LinkedIn
              </a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="share-button facebook">
                Facebook
              </a>
            </div>
          </div>
          
          <div className="author-bio">
            <div className="author-info">
              <h4>Sobre o autor</h4>
              <p>{post.author} é especialista em {post.category} na UpdateCommit.</p>
            </div>
          </div>
        </div>
      </div>
      
      {relatedPosts.length > 0 && (
        <div className="related-posts">
          <h3>Leia também</h3>
          <div className="related-posts-grid">
            {relatedPosts.map(relatedPost => (
              <div key={relatedPost.id} className="related-post-card">
                <Link to={`/blog/${relatedPost.slug}`}>
                  <div className="related-post-image">
                    <img src={relatedPost.image} alt={relatedPost.title} />
                  </div>
                  <div className="related-post-content">
                    <h4>{relatedPost.title}</h4>
                    <p>{relatedPost.summary}</p>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="call-to-action-section">
        <h3>Precisa de ajuda com soluções digitais para o seu negócio?</h3>
        <p>Nossa equipe de especialistas está pronta para ajudar sua empresa a crescer com as melhores estratégias e ferramentas.</p>
        <Link to="/contato" className="cta-button">Fale Conosco</Link>
      </div>
    </div>
  );
};

export default BlogPost;